import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { Button } from '@/components/ui/button'
import { MediaSlider } from '@/components/media/MediaSlider'
import { MediaReelsViewer } from '@/components/media/MediaReelsViewer'
import { MediaVideoModal } from '@/components/media/MediaVideoModal'
import { useMediaHomepage } from '@/hooks/api/useMedia'
import { isInstagramReel, openReelsViewer } from '@/lib/mediaUtils'
import { trackMediaEvent } from '@/lib/analytics/mediaEvents'
import { PATHS } from '@/routes/paths'
import type { MediaVideo } from '@/types/media'

export function MediaInsightsSection() {
  const { items, isLoading } = useMediaHomepage(12)
  const [reelsOpen, setReelsOpen] = useState(false)
  const [reelsStartIndex, setReelsStartIndex] = useState(0)
  const [modalVideo, setModalVideo] = useState<MediaVideo | null>(null)

  const reelVideos = useMemo(
    () => items.filter((v) => isInstagramReel(v) || v.platform === 'instagram'),
    [items],
  )

  const handlePlay = (video: MediaVideo) => {
    if (video.platform === 'instagram') {
      openReelsViewer(video, reelVideos, setReelsStartIndex, setReelsOpen)
      return
    }
    setModalVideo(video)
  }

  if (!isLoading && !items.length) return null

  return (
    <>
      <div className="bg-page">
        <Container>
          <AnimateIn>
            <Section
              title="Latest Property Insights"
              subtitle="Watch expert advice, market updates and property investment tips."
              action={
                <Button asChild variant="ghost" size="sm" className="hidden sm:flex">
                  <Link
                    to={PATHS.media}
                    onClick={() => trackMediaEvent('media_view_all', { source: 'homepage_header' })}
                  >
                    View All <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              }
            >
              <MediaSlider videos={items} isLoading={isLoading} onPlay={handlePlay} />
            </Section>
          </AnimateIn>
        </Container>
      </div>

      {reelsOpen && reelVideos.length ? (
        <MediaReelsViewer
          videos={reelVideos}
          startIndex={reelsStartIndex}
          onClose={() => setReelsOpen(false)}
        />
      ) : null}

      <MediaVideoModal video={modalVideo} onClose={() => setModalVideo(null)} />
    </>
  )
}
